import { Box, Spinner, Text } from "@primer/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appConst } from "../../AppConst";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAuth } from "../../hooks/useAuth";
import { rootActions } from "../../state/actions/rootActions";

/**
 * Clone Old `Account/Login.cshtml` — split card 900px:
 *   [Left: branding webName + tagline] | [Right: form đăng nhập]
 *
 * Form fields exact giống Old: username (mã SV) + password (show/hide eye toggle) + alert warning.
 * CAPTCHA reserved nhưng skip render vì backend mới chưa support.
 */
const LoginPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, errorMessage } = useAuth();
    const { webName } = appConst.branding;

    const [maSv, setMaSv] = useState("");
    const [matKhau, setMatKhau] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (isAuthenticated) navigate("/", { replace: true });
    }, [isAuthenticated, navigate]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!maSv.trim() || !matKhau) return;
        dispatch(rootActions.auth.LOGIN_START({ ma_sv: maSv.trim(), mat_khau: matKhau }));
    };

    return (
        <Box
            id="Login"
            sx={{
                bg: "#E7E5EE",
                width: "100vw",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
            }}
        >
            <Box
                id="formLogin"
                sx={{
                    bg: "#fff",
                    width: 900,
                    maxWidth: "95%",
                    p: ["20px 15px", "35px 25px"],
                    border: "1px solid #ccc",
                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                    display: "flex",
                    flexDirection: ["column", "column", "row"],
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: [3, 3, 0],
                    borderRadius: "20px",
                    fontFamily:
                        'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif',
                }}
                onKeyUp={(e) => {
                    if (e.key === "Enter") handleSubmit(e as any);
                }}
            >
                {/* Left — branding */}
                <Box
                    className="left"
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        py: [2, 3],
                    }}
                >
                    <Box className="nameWebsite">
                        <Text
                            sx={{
                                fontSize: 17,
                                display: "block",
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                color: "#333",
                                mb: 1,
                            }}
                        >
                            {webName}
                        </Text>
                        <Text
                            sx={{
                                fontSize: 17,
                                display: "block",
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                color: "#333",
                            }}
                        >
                            Cổng thông tin đăng ký tín chỉ
                        </Text>
                    </Box>
                </Box>

                {/* Right — form */}
                <Box
                    className="right"
                    sx={{
                        width: ["100%", "100%", 360],
                        ml: [0, 0, 3],
                        borderLeft: ["none", "none", "1px solid #f2f2f2"],
                        pl: [0, 0, 3],
                        maxWidth: "100%",
                    }}
                >
                    <Text
                        className="title"
                        sx={{
                            display: "block",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            fontSize: 17,
                            textAlign: "center",
                            mb: 3,
                            color: "#333",
                        }}
                    >
                        Đăng nhập tài khoản
                    </Text>

                    <Box as="form" onSubmit={handleSubmit} className="main">
                        {/* Mã sinh viên */}
                        <Box className="rowForm" sx={{ my: 2 }}>
                            <Text
                                as="p"
                                sx={{
                                    mb: "5px",
                                    fontWeight: "bold",
                                    fontSize: 14,
                                    color: "#333",
                                }}
                            >
                                Mã sinh viên <Text sx={{ color: "red" }}>(*)</Text>
                            </Text>
                            <input
                                type="text"
                                className="old-form-control"
                                id="username"
                                value={maSv}
                                onChange={(e) => setMaSv(e.target.value)}
                                disabled={isLoading}
                                autoFocus
                            />
                        </Box>

                        {/* Mật khẩu với show/hide */}
                        <Box className="rowForm" sx={{ my: 2 }}>
                            <Text
                                as="p"
                                sx={{
                                    mb: "5px",
                                    fontWeight: "bold",
                                    fontSize: 14,
                                    color: "#333",
                                }}
                            >
                                Mật khẩu <Text sx={{ color: "red" }}>(*)</Text>
                            </Text>
                            <div className="old-show-hide-password">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    value={matKhau}
                                    onChange={(e) => setMatKhau(e.target.value)}
                                    disabled={isLoading}
                                />
                                <div className="input-group-addon">
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(v => !v)}
                                        aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                                        style={{
                                            background: "transparent",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: 0,
                                        }}
                                    >
                                        <i
                                            className={`fa ${
                                                showPassword ? "fa-eye" : "fa-eye-slash"
                                            }`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            </div>
                        </Box>

                        {/* Error message */}
                        {errorMessage && (
                            <div
                                className="old-alert"
                                style={{
                                    background: "#f8d7da",
                                    borderColor: "#f5c6cb",
                                    color: "#721c24",
                                    marginTop: 8,
                                    marginBottom: 8,
                                }}
                            >
                                {errorMessage}
                            </div>
                        )}

                        {/* Warning note */}
                        <div className="old-alert old-alert-warning" style={{ padding: "4px 11px" }}>
                            Lưu ý: Sinh viên đổi mật khẩu trước khi đăng ký
                            <br />
                            Mật khẩu bao gồm chữ, số, ký tự đặc biệt.
                        </div>

                        {/* Submit */}
                        <Box
                            className="rowForm"
                            sx={{
                                my: 2,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <button
                                type="submit"
                                className="old-btn old-btn-primary"
                                style={{ width: "100%", fontWeight: "bold", padding: "8px 10px" }}
                                disabled={isLoading || !maSv.trim() || !matKhau}
                            >
                                {isLoading ? <Spinner size="small" /> : "Đăng nhập"}
                            </button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default LoginPage;
